import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { Drawer } from '@mercai/clever'

import { formatDateWithTime } from '@/helpers'

import {
  useAICreateConversation,
  useAIUpdateConversation,
  useAICreateWhatsappMessage,
  useAIConversationDetail,
  QUERIES_ADMIN,
} from '@/services'

import { Chat, Header } from './components'

import type { IAMessageProps } from '@/types'
import type { DrawerChatProps } from './drawer-chat.type'

import styles from './drawer-chat.module.css'
import { useAuth } from '@/providers'

export const ConversationDrawerChat = ({
  uuid,
  onChangeUuid,
  email,
  isOpen,
  onClose,
}: DrawerChatProps) => {
  const { t } = useTranslation('screenAiCenter')

  const queryClient = useQueryClient()

  const [conversationUuid, setConversationUuid] = useState<string>('')
  const [messages, setMessages] = useState<Partial<IAMessageProps>[]>([])

  const { whoAmI } = useAuth()

  const { mutateAsync: createConversation, isPending: isCreatingConversation } =
    useAICreateConversation()

  const { mutateAsync: createMessage, isPending: isCreatingMessage } =
    useAICreateWhatsappMessage()

  const { data: conversationDetail, refetch: refetchConversationDetail } =
    useAIConversationDetail(uuid, email, {
      refetchInterval: isOpen ? 3000 : undefined,
    })

  const { mutateAsync: updateConversation, isPending: isUpdatingConversation } =
    useAIUpdateConversation()

  const onInitConversation = async (content: string) => {
    const responseConversation = await createConversation({
      createdBy: whoAmI?.email || '',
    })

    if (responseConversation.success) {
      setConversationUuid(responseConversation.data?.uuid || '')
      onChangeUuid?.(responseConversation.data?.uuid || '')
      const responseMessage = await createMessage({
        conversationUuid: responseConversation.data?.uuid || '',
        content,
        author: 'assistant',
      })

      if (responseMessage.success) {
        setMessages([
          ...messages,
          {
            conversationUuid: responseConversation.data?.uuid || '',
            content,
            author: 'assistant',
            messageType: 'text',
            createdAt: new Date(),
          },
        ])

        queryClient.invalidateQueries({
          queryKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
        })
      }
    }
  }

  const onContinueConversation = async (content: string) => {
    const responseMessage = await createMessage({
      conversationUuid,
      content,
    })

    if (responseMessage.success && responseMessage.data) {
      setMessages([
        ...messages,
        {
          conversationUuid,
          content,
          author: 'assistant',
          messageType: 'text',
          createdAt: new Date(),
        },
      ])
    }
  }

  const onSendMessage = async (message: string) => {
    if (conversationUuid) {
      onContinueConversation(message)
    } else {
      onInitConversation(message)
    }
  }

  const onToggleAiPaused = async () => {
    await updateConversation({
      uuid: conversationUuid,
      isAiPaused: !conversationDetail?.data?.isAiPaused,
    })

    refetchConversationDetail()
  }

  useEffect(() => {
    if (conversationDetail?.success && conversationDetail?.data) {
      setMessages(conversationDetail.data.messages)
      setConversationUuid(conversationDetail.data.uuid)
    }
  }, [conversationDetail, isOpen])

  useEffect(() => {
    if (!isOpen) {
      setMessages([])
      setConversationUuid('')
    }

    return () => {
      setMessages([])
      setConversationUuid('')
    }
  }, [isOpen])

  const isExpired = useMemo(() => {
    if (conversationDetail?.data?.status === 'inactive') {
      return true
    }

    const lastMessage =
      conversationDetail?.data?.messages?.[
        conversationDetail?.data?.messages.length - 1
      ]

    return lastMessage?.createdAt
      ? new Date(lastMessage.createdAt) <
          new Date(Date.now() - 1000 * 60 * 60 * 24)
      : false
  }, [conversationDetail])

  return (
    <Drawer isOpen={isOpen || false} onHandleOpen={() => onClose?.()}>
      <div className={styles.core}>
        <Header
          title={`${t('drawerChatHeaderTitle')} ${formatDateWithTime(
            conversationDetail?.data?.createdAt || new Date(),
          )}`}
          description={`${t('drawerChatHeaderDescription')} ${conversationDetail?.data?.createdBy || ''}`}
          isAiPaused={conversationDetail?.data?.isAiPaused || false}
          onToggleAiPaused={onToggleAiPaused}
          buttonAiPaused={t('drawerChatPausedAIButtonActive')}
          isAiPausing={isUpdatingConversation}
        />

        <Chat
          messages={messages as IAMessageProps[]}
          onSendMessage={onSendMessage}
          isLoading={isCreatingConversation || isCreatingMessage}
          origin={conversationDetail?.data?.origin || ''}
          isAiPaused={conversationDetail?.data?.isAiPaused || false}
          isAiPausing={isUpdatingConversation}
          onToggleAiPaused={onToggleAiPaused}
          isExpired={isExpired}
        />
      </div>
    </Drawer>
  )
}
